//! Tab "Risoluzione" della pagina dettaglio market.
//!
//! Mostra:
//!  · stato corrente del market (open → closed → resolved)
//!  · oracle source + finestra di risoluzione (resolve_after)
//!  · winning outcome (se risolto)
//!  · timeline degli attempt dell'oracolo (proposing → proposed → finalized)
//!  · proof JSON espandibile per ciascun attempt
//!  · countdown al termine del dispute window per l'ultimo proposed

import { useEffect, useState } from "react";

import { Icon } from "@/components/atoms/Icons";
import { Pill } from "@/components/atoms/Pill";
import { fmtShortDate } from "@/components/atoms/format";
import { ProviderEvidence } from "@/components/market/ProviderEvidence";
import { useResolution, type ResolutionAttempt } from "@/components/market/useResolution";
import type { components } from "@/api/schema.gen";

type MarketView = components["schemas"]["MarketView"];

const STATUS_LABEL: Record<string, string> = {
  created: "Creato",
  open: "Aperto",
  closed: "Chiuso, in attesa di oracolo",
  resolved: "Risolto",
  settled: "Liquidato",
  voided: "Annullato",
};

const ATTEMPT_LABEL: Record<string, string> = {
  proposing: "Richiesta inviata",
  proposed: "Risposta dell'oracolo",
  disputed: "Disputa in corso",
  finalized: "Confermato on-chain",
  failed: "Fallito",
};

const ATTEMPT_VARIANT: Record<string, "accent" | "yes" | "no" | "warn"> = {
  proposing: "accent",
  proposed: "warn",
  disputed: "no",
  finalized: "yes",
  failed: "no",
};

export function ResolutionPanel({ market }: { market: MarketView }) {
  const { data, loading } = useResolution(market.slug, 15_000);

  if (loading && !data) {
    return (
      <div className="card flex h-[240px] animate-pulse items-center justify-center p-5 text-[12px] text-muted">
        Caricamento stato risoluzione…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="card p-5 text-[13px] text-muted">
        Stato risoluzione non disponibile.
      </div>
    );
  }

  const winning = data.winning_outcome_id
    ? market.outcomes.find((o) => o.id === data.winning_outcome_id)
    : null;

  return (
    <div className="flex flex-col gap-3">
      {/* Header: stato + sorgente */}
      <div className="card flex flex-col gap-4 p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-[11px] uppercase tracking-wider text-muted">
              Stato risoluzione
            </span>
            <span className="text-[18px] font-semibold">
              {STATUS_LABEL[data.market_status] ?? data.market_status}
            </span>
          </div>
          <div className="flex flex-col items-end gap-1 text-right">
            <span className="text-[11px] uppercase tracking-wider text-muted">
              Oracolo
            </span>
            <Pill variant="accent">{data.resolution_source}</Pill>
          </div>
        </div>

        {winning && (
          <div className="rounded-card border border-line-strong bg-bg-2 p-4">
            <div className="flex items-center gap-2 text-[12px] text-muted">
              <Icon.Check size={14} />
              Outcome vincente
            </div>
            <div
              className="mt-1 font-mono text-[22px] font-semibold"
              style={{ color: "var(--yes)" }}
            >
              {winning.label}
            </div>
          </div>
        )}

        {data.dispute_window_ends_at && (
          <DisputeCountdown endsAt={data.dispute_window_ends_at} />
        )}

        <div className="grid grid-cols-2 gap-3 text-[12px] sm:grid-cols-3">
          <Meta label="Apertura">{fmtShortDate(market.open_at)}</Meta>
          <Meta label="Chiusura">{fmtShortDate(market.close_at)}</Meta>
          <Meta label="Oracolo da">{fmtShortDate(market.resolve_after)}</Meta>
        </div>

        <div className="text-[13px] text-text-2">
          <span className="text-muted">Criterio: </span>
          {market.description}
        </div>
      </div>

      {/* Timeline degli attempt */}
      <div className="card p-0">
        <div className="flex items-center justify-between border-b border-line px-4 py-2">
          <span className="text-[11px] uppercase tracking-wider text-muted">
            Tentativi oracolo ({data.attempts.length})
          </span>
        </div>
        {data.attempts.length === 0 ? (
          <div className="px-4 py-6 text-center text-[12.5px] text-muted">
            Nessun tentativo ancora. L'oracolo verrà chiamato il{" "}
            <span className="text-text-2">{fmtShortDate(market.resolve_after)}</span>.
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {[...data.attempts].reverse().map((a) => (
              <AttemptRow key={a.attempt_no} attempt={a} market={market} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ─── countdown al dispute window ─────────────────────────────────────

function DisputeCountdown({ endsAt }: { endsAt: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const ms = new Date(endsAt).getTime() - now;
  const remaining = Math.max(0, Math.floor(ms / 1000));
  const expired = remaining === 0;
  const m = Math.floor(remaining / 60);
  const s = remaining % 60;
  return (
    <div className="rounded-card border border-line-strong bg-bg-2 p-4">
      <div className="flex items-center gap-2 text-[12px] text-muted">
        <Icon.Clock size={14} />
        Finestra di disputa
      </div>
      <div className="mt-1 font-mono text-[20px] font-semibold">
        {expired ? (
          <span style={{ color: "var(--yes)" }}>Chiusa, in finalizzazione…</span>
        ) : (
          <span>
            {m}m {s.toString().padStart(2, "0")}s
          </span>
        )}
      </div>
      <div className="mt-1 text-[11px] text-muted">
        Termina alle {new Date(endsAt).toLocaleTimeString()}
      </div>
    </div>
  );
}

// ─── attempt row ─────────────────────────────────────────────────────

function AttemptRow({
  attempt,
  market,
}: {
  attempt: ResolutionAttempt;
  market: MarketView;
}) {
  const [expanded, setExpanded] = useState(false);
  const variant = ATTEMPT_VARIANT[attempt.status] ?? "accent";
  const label = ATTEMPT_LABEL[attempt.status] ?? attempt.status;

  const ts =
    attempt.finalized_at ??
    attempt.proposed_at ??
    null;

  const winning = attempt.winning_outcome_id
    ? market.outcomes.find((o) => o.id === attempt.winning_outcome_id)
    : null;

  const txHash = attempt.onchain_tx_hash ?? txHashFromProof(attempt.proof);

  return (
    <li className="px-4 py-3 text-[12.5px]">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="font-mono text-muted">#{attempt.attempt_no}</span>
          <Pill variant={variant}>{label}</Pill>
          <span className="text-muted">{attempt.source}</span>
        </div>
        <div className="flex items-center gap-3 text-muted">
          {ts && <span className="font-mono">{new Date(ts).toLocaleString()}</span>}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-text-2 hover:text-text"
            aria-label="toggle details"
          >
            <Icon.ChevronDown
              size={14}
              style={{ transform: expanded ? "rotate(180deg)" : "none" }}
            />
          </button>
        </div>
      </div>

      {winning && (
        <div className="mt-2 text-[12.5px]">
          <span className="text-muted">Vincente proposto: </span>
          <span className="font-semibold text-text">{winning.label}</span>
        </div>
      )}

      {attempt.failure_reason && (
        <div className="mt-2 rounded-card border border-line bg-bg-2 px-3 py-2 text-[12px]">
          <span className="text-muted">Motivo fallimento: </span>
          <span style={{ color: "var(--no)" }}>{attempt.failure_reason}</span>
        </div>
      )}

      {expanded && (
        <div className="mt-3 flex flex-col gap-3">
          {txHash && (
            <div className="flex items-center justify-between rounded-card border border-line bg-bg-2 px-3 py-2 text-[12px]">
              <span className="text-muted">Tx on-chain</span>
              <a
                href={`https://polygonscan.com/tx/${txHash}`}
                target="_blank"
                rel="noreferrer"
                className="font-mono text-accent hover:underline"
              >
                {shortHash(txHash)} <span aria-hidden>↗</span>
              </a>
            </div>
          )}
          <ProviderEvidence proof={attempt.proof} />
          {attempt.proof ? (
            <details className="rounded-card border border-line bg-bg-2 p-3">
              <summary className="cursor-pointer text-[11px] uppercase tracking-wider text-muted">
                Proof JSON raw
              </summary>
              <pre className="mt-2 max-h-[280px] overflow-auto font-mono text-[11.5px] leading-[1.5] text-text-2">
                {JSON.stringify(attempt.proof, null, 2)}
              </pre>
            </details>
          ) : (
            <div className="text-[11.5px] text-muted">Nessun proof allegato.</div>
          )}
        </div>
      )}
    </li>
  );
}

// ─── helpers ─────────────────────────────────────────────────────────

function Meta({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10.5px] uppercase tracking-wider text-muted">
        {label}
      </span>
      <span className="font-mono text-text-2">{children}</span>
    </div>
  );
}

function shortHash(h: string): string {
  if (h.length <= 14) return h;
  return `${h.slice(0, 8)}…${h.slice(-6)}`;
}

/** Cerca un tx hash dentro al proof JSON (cheap heuristic: cerca chiavi note). */
function txHashFromProof(proof: unknown): string | null {
  if (!proof || typeof proof !== "object") return null;
  const obj = proof as Record<string, unknown>;
  for (const key of ["tx_hash", "txHash", "onchain_tx_hash", "transactionHash"]) {
    const v = obj[key];
    if (typeof v === "string" && v.startsWith("0x")) return v;
  }
  return null;
}
